// src/components/QRScanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  FaQrcode, 
  FaClock, 
  FaShieldAlt, 
  FaDownload, 
  FaShare, 
  FaSpinner,
  FaPrint,
  FaBuilding,
  FaUser,
  FaTrash,
  FaEdit,
  FaPlus,
  FaSync,
  FaTimes,
  FaMapMarkerAlt,
  FaSpinner as FaSpinnerIcon,
  FaEye,
  FaSearch,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';

const QRScanner = () => {
  // ─── FORM STATES ───
  const [formData, setFormData] = useState({
    companyName: '',
    locationName: '',
    address: '',
    latitude: '',
    longitude: '',
    adminName: '',
    expiryTime: 30,
    qrSize: 200
  });

  const [savedLocations, setSavedLocations] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  // ─── API LOCATIONS STATES ───
  const [apiLocations, setApiLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [apiLocationError, setApiLocationError] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApiLocation, setSelectedApiLocation] = useState(null);

  // Popup states
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // QR Generator States
  const [qrToken, setQrToken] = useState('');
  const [qrExpiry, setQrExpiry] = useState(30);
  const [qrData, setQrData] = useState('');
  const qrTimerRef = useRef(null);

  // View QR state
  const [viewingQR, setViewingQR] = useState(null);

  // Refs for QR canvas
  const qrCanvasRefs = useRef({});
  const dropdownRef = useRef(null);

  // Load saved locations from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('qrLocations');
    if (saved) {
      setSavedLocations(JSON.parse(saved));
    }
  }, []);

  // ─── FETCH API LOCATIONS ───
  const fetchApiLocations = async () => {
    setIsLoadingLocations(true);
    setApiLocationError('');
    
    try {
      const response = await fetch('http://localhost:5001/api/location/alllocation', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add any auth tokens if needed
          // 'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data); // For debugging

      if (data.locations && Array.isArray(data.locations)) {
        setApiLocations(data.locations);
        setApiLocationError('');
      } else if (data.locations && Array.isArray(data.locations)) {
        setApiLocations(data.locations);
      } else {
        setApiLocationError('Invalid data format received from server');
        setApiLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setApiLocationError(error.message || 'Failed to fetch locations from API');
      setApiLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // ─── HANDLE API LOCATION SELECT ───
  const handleApiLocationSelect = (location) => {
    setSelectedApiLocation(location);
    
    // Extract company name from location name (split by comma or dot)
    let companyName = location.name || '';
    let locationName = location.name || '';
    
    // Try to extract meaningful company name
    if (companyName.includes(',')) {
      const parts = companyName.split(',');
      companyName = parts[0].trim();
      locationName = parts.slice(1).join(',').trim();
    } else if (companyName.includes('.')) {
      const parts = companyName.split('.');
      companyName = parts[0].trim();
      locationName = parts.slice(1).join('.').trim();
    }
    
    // If no comma or dot, use the full name as company name
    if (companyName === locationName) {
      companyName = location.name;
      locationName = 'Main Office';
    }

    setFormData(prev => ({
      ...prev,
      companyName: companyName,
      locationName: locationName || 'Main Office',
      address: location.fullAddress || '',
      latitude: location.latitude ? location.latitude.toString() : '',
      longitude: location.longitude ? location.longitude.toString() : '',
    }));

    setSearchTerm(location.name);
    setShowLocationDropdown(false);
  };

  // ─── GET CURRENT LOCATION ───
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        setFormData(prev => ({
          ...prev,
          latitude: latitude.toString(),
          longitude: longitude.toString()
        }));

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          const data = await response.json();
          
          if (data && data.display_name) {
            setFormData(prev => ({
              ...prev,
              address: data.display_name
            }));
          }
        } catch (error) {
          console.error('Error fetching address:', error);
        }

        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Location error:', error);
        setLocationError(error.message || 'Failed to get location');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // ─── FORM HANDLERS ───
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowLocationDropdown(true);
    
    // If user types manually, clear selected API location
    if (selectedApiLocation) {
      setSelectedApiLocation(null);
    }
  };

  const handleGenerateQR = () => {
    if (!formData.companyName || !formData.locationName) {
      alert('Please fill in Company Name and Location Name');
      return;
    }

    setIsGenerating(true);
    
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const token = `${formData.companyName.substring(0, 3).toUpperCase()}-${timestamp}-${random}`;
    setQrToken(token);
    setQrExpiry(formData.expiryTime || 30);

    const qrPayload = JSON.stringify({
      type: 'ATTENDANCE_QR',
      company: formData.companyName,
      location: formData.locationName,
      token: token,
      timestamp: Date.now(),
      validUntil: new Date(Date.now() + (formData.expiryTime || 30) * 1000).toISOString(),
      apiLocationId: selectedApiLocation?._id || null,
      fullAddress: formData.address || ''
    });
    setQrData(qrPayload);

    const locationEntry = {
      id: editingId || Date.now(),
      companyName: formData.companyName,
      locationName: formData.locationName,
      address: formData.address,
      latitude: formData.latitude,
      longitude: formData.longitude,
      adminName: formData.adminName,
      token: token,
      qrData: qrPayload,
      createdAt: new Date().toISOString(),
      apiLocationId: selectedApiLocation?._id || null
    };
    
    let updatedLocations;
    if (editingId) {
      updatedLocations = savedLocations.map(l => l.id === editingId ? locationEntry : l);
    } else {
      updatedLocations = [locationEntry, ...savedLocations];
    }
    
    setSavedLocations(updatedLocations);
    localStorage.setItem('qrLocations', JSON.stringify(updatedLocations));

    resetForm();
    setIsPopupOpen(false);
    setEditingId(null);
    setSelectedApiLocation(null);

    setTimeout(() => {
      setIsGenerating(false);
    }, 500);
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      locationName: '',
      address: '',
      latitude: '',
      longitude: '',
      adminName: '',
      expiryTime: 30,
      qrSize: 200
    });
    setQrToken('');
    setQrData('');
    setLocationError('');
    setSearchTerm('');
    setSelectedApiLocation(null);
    setShowLocationDropdown(false);
  };

  const handleEditLocation = (location) => {
    setFormData({
      companyName: location.companyName || '',
      locationName: location.locationName || '',
      address: location.address || '',
      latitude: location.latitude || '',
      longitude: location.longitude || '',
      adminName: location.adminName || '',
      expiryTime: 30,
      qrSize: 200
    });
    setEditingId(location.id);
    setIsPopupOpen(true);
  };

  const handleDeleteLocation = (id) => {
    if (window.confirm('Delete this QR code?')) {
      const updated = savedLocations.filter(l => l.id !== id);
      setSavedLocations(updated);
      localStorage.setItem('qrLocations', JSON.stringify(updated));
    }
  };

  const handleViewQR = (location) => {
    setViewingQR(location);
  };

  const closeViewQR = () => {
    setViewingQR(null);
  };

  const openPopup = () => {
    resetForm();
    setEditingId(null);
    setIsPopupOpen(true);
    // Fetch API locations when popup opens
    fetchApiLocations();
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setEditingId(null);
    resetForm();
    setApiLocations([]);
  };

  // ─── CLOSE DROPDOWN ON CLICK OUTSIDE ───
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── CONVERT SVG TO CANVAS ───
  const svgToCanvas = (svgElement) => {
    return new Promise((resolve, reject) => {
      try {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const width = svgElement.getAttribute('width') || 300;
        const height = svgElement.getAttribute('height') || 300;
        canvas.width = parseInt(width);
        canvas.height = parseInt(height);
        
        const img = new Image();
        img.onload = function() {
          ctx.drawImage(img, 0, 0);
          resolve(canvas);
        };
        img.onerror = function() {
          reject(new Error('Failed to load SVG'));
        };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // ─── GET QR CANVAS ───
  const getQRCanvas = async (qrId) => {
    try {
      const svgElement = document.querySelector(`#${qrId}`);
      if (svgElement) {
        return await svgToCanvas(svgElement);
      }
      
      const svgElements = document.querySelectorAll('svg');
      for (let svg of svgElements) {
        if (svg.id === qrId || svg.closest(`[data-qr-id="${qrId}"]`)) {
          return await svgToCanvas(svg);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting QR canvas:', error);
      return null;
    }
  };

  // ─── CREATE QR IMAGE FOR DOWNLOAD/PRINT ───
  const createQRImage = (companyName, qrCanvas) => {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const qrSize = 300;
        const padding = 30;
        const textHeight = 70;
        
        canvas.width = qrSize + padding * 2;
        canvas.height = qrSize + padding * 2 + textHeight;
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 2;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);
        
        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(companyName, canvas.width / 2, textHeight / 2 + 4);
        
        if (qrCanvas) {
          ctx.drawImage(qrCanvas, padding, textHeight, qrSize, qrSize);
        }
        
        resolve(canvas);
      } catch (error) {
        reject(error);
      }
    });
  };

  // ─── DOWNLOAD QR ───
  const downloadQR = async (companyName, qrId) => {
    try {
      const qrCanvas = await getQRCanvas(qrId);
      if (!qrCanvas) {
        alert('QR code not found. Please generate QR first.');
        return;
      }

      const finalCanvas = await createQRImage(companyName, qrCanvas);
      if (!finalCanvas) {
        alert('Error creating QR image.');
        return;
      }

      const link = document.createElement('a');
      link.download = `qr-${companyName.replace(/\s+/g, '-')}-${Date.now()}.png`;
      link.href = finalCanvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      alert('Error downloading QR. Please try again.');
    }
  };

  // ─── PRINT QR ───
  const printQR = async (companyName, qrId) => {
    try {
      const qrCanvas = await getQRCanvas(qrId);
      if (!qrCanvas) {
        alert('QR code not found. Please generate QR first.');
        return;
      }

      const finalCanvas = await createQRImage(companyName, qrCanvas);
      if (!finalCanvas) {
        alert('Error creating QR image.');
        return;
      }

      const printWindow = window.open('', '_blank', 'width=500,height=600');
      if (!printWindow) {
        alert('Please allow popups for this site');
        return;
      }

      const imageData = finalCanvas.toDataURL('image/png', 1.0);
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR - ${companyName}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body {
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                background: white;
                font-family: Arial, sans-serif;
              }
              .qr-container {
                text-align: center;
                padding: 20px;
              }
              img {
                max-width: 100%;
                height: auto;
                display: block;
                margin: 0 auto;
              }
              @media print {
                body { margin: 0; padding: 20px; }
                img { max-width: 100%; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <img src="${imageData}" alt="QR Code" />
              <button class="no-print" onclick="window.print()" style="margin-top:20px;padding:10px 30px;font-size:16px;cursor:pointer;background:#4f46e5;color:white;border:none;border-radius:8px;">
                🖨️ Print
              </button>
              <button class="no-print" onclick="window.close()" style="margin-top:20px;padding:10px 30px;font-size:16px;cursor:pointer;background:#e2e8f0;color:#333;border:none;border-radius:8px;margin-left:10px;">
                Close
              </button>
            </div>
            <script>
              setTimeout(() => {
                window.print();
              }, 1000);
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Print error:', error);
      alert('Error printing QR. Please try again.');
    }
  };

  // ─── SHARE QR ───
  const shareQR = async (companyName, qrId) => {
    try {
      const qrCanvas = await getQRCanvas(qrId);
      if (!qrCanvas) {
        alert('QR code not found. Please generate QR first.');
        return;
      }

      const finalCanvas = await createQRImage(companyName, qrCanvas);
      if (!finalCanvas) {
        alert('Error creating QR image.');
        return;
      }

      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      
      if (navigator.share) {
        await navigator.share({
          title: `QR - ${companyName}`,
          text: `Scan QR for attendance - ${companyName}`,
          files: [new File([blob], `qr-${companyName}.png`, { type: 'image/png' })]
        });
      } else {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob
            })
          ]);
          alert('✅ QR code image copied to clipboard!');
        } catch (clipboardError) {
          const link = document.createElement('a');
          link.download = `qr-${companyName.replace(/\s+/g, '-')}.png`;
          link.href = finalCanvas.toDataURL('image/png');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          alert('QR code downloaded. Share it manually.');
        }
      }
    } catch (error) {
      console.error('Share failed:', error);
      alert('Error sharing QR. Please try again.');
    }
  };

  // ─── FILTER LOCATIONS BASED ON SEARCH ───
  const filteredLocations = apiLocations.filter(location =>
    location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.fullAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ─── RENDER ───
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/80 to-purple-50/60 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/85 backdrop-blur-2xl rounded-3xl shadow-xl shadow-indigo-500/5 border border-white/60 overflow-hidden hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-500">
          <div className="p-6">
            {/* Header with Add Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center">
                  <FaQrcode className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">QR Code Manager</h3>
                  <p className="text-xs text-gray-500">Manage attendance QR codes</p>
                </div>
              </div>
              <button
                onClick={openPopup}
                className="px-4 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/25 transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-95"
              >
                <FaPlus className="w-4 h-4" />
                Add QR Code
              </button>
            </div>

            {/* ─── QR CODES LIST ─── */}
            {savedLocations.length === 0 ? (
              <div className="text-center py-16 bg-gray-50/80 rounded-2xl border-2 border-dashed border-gray-200">
                <FaQrcode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No QR Codes Generated Yet</p>
                <p className="text-sm text-gray-400 mt-1">Click "Add QR Code" to create one</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedLocations.map((loc) => (
                  <div key={loc.id} className="bg-white/90 rounded-2xl border border-gray-200 p-4 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex flex-col items-center">
                      <div className="text-center mb-2">
                        <p className="text-sm font-bold text-gray-800 truncate w-full">{loc.companyName}</p>
                        <p className="text-[10px] text-gray-500">{loc.locationName}</p>
                      </div>
                      <div className="relative bg-white rounded-xl p-2 border border-gray-100">
                        <QRCodeSVG 
                          id={`qr-${loc.id}`}
                          value={loc.qrData || JSON.stringify({ company: loc.companyName, location: loc.locationName })}
                          size={120}
                          level="H"
                          includeMargin={false}
                          bgColor="white"
                          fgColor="#1e293b"
                        />
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
                        <button
                          onClick={() => handleViewQR(loc)}
                          className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-500 transition-colors"
                          title="View"
                        >
                          <FaEye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => downloadQR(loc.companyName, `qr-${loc.id}`)}
                          className="p-1.5 rounded-lg hover:bg-indigo-100 text-indigo-500 transition-colors"
                          title="Download"
                        >
                          <FaDownload className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => printQR(loc.companyName, `qr-${loc.id}`)}
                          className="p-1.5 rounded-lg hover:bg-green-100 text-green-500 transition-colors"
                          title="Print"
                        >
                          <FaPrint className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => shareQR(loc.companyName, `qr-${loc.id}`)}
                          className="p-1.5 rounded-lg hover:bg-cyan-100 text-cyan-500 transition-colors"
                          title="Share"
                        >
                          <FaShare className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleEditLocation(loc)}
                          className="p-1.5 rounded-lg hover:bg-yellow-100 text-yellow-500 transition-colors"
                          title="Edit"
                        >
                          <FaEdit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteLocation(loc.id)}
                          className="p-1.5 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
                          title="Delete"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="mt-2 text-[8px] text-gray-400">
                        {loc.createdAt ? new Date(loc.createdAt).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── ADD QR POPUP ─── */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaPlus className="text-indigo-500" />
                {editingId ? 'Edit QR Code' : 'Add New QR Code'}
              </h3>
              <button
                onClick={closePopup}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6">
              {/* ─── API LOCATION SEARCH ─── */}
              <div className="mb-4" ref={dropdownRef}>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  🔍 Search Location from Database
                </label>
                <div className="relative">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => {
                        setShowLocationDropdown(true);
                        if (apiLocations.length === 0) {
                          fetchApiLocations();
                        }
                      }}
                      placeholder="Type to search locations..."
                      className="w-full pl-9 pr-10 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                    />
                    {isLoadingLocations && (
                      <FaSpinnerIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-500 w-4 h-4 animate-spin" />
                    )}
                    {!isLoadingLocations && apiLocations.length > 0 && (
                      <button
                        onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showLocationDropdown ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                      </button>
                    )}
                  </div>

                  {/* Dropdown */}
                  {showLocationDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {isLoadingLocations ? (
                        <div className="p-4 text-center text-gray-500">
                          <FaSpinnerIcon className="w-5 h-5 animate-spin mx-auto mb-2" />
                          Loading locations...
                        </div>
                      ) : apiLocationError ? (
                        <div className="p-4 text-center">
                          <p className="text-red-500 text-sm">{apiLocationError}</p>
                          <button
                            onClick={fetchApiLocations}
                            className="mt-2 text-indigo-500 text-sm font-medium hover:text-indigo-600"
                          >
                            Retry
                          </button>
                        </div>
                      ) : filteredLocations.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {searchTerm ? 'No locations found' : 'No locations available'}
                        </div>
                      ) : (
                        filteredLocations.map((location) => (
                          <div
                            key={location._id}
                            onClick={() => handleApiLocationSelect(location)}
                            className="px-4 py-3 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="flex items-start gap-2">
                              <FaBuilding className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-gray-800">{location.name}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{location.fullAddress}</p>
                                {location.isActive !== undefined && (
                                  <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full mt-1 ${location.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {location.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {selectedApiLocation && (
                  <p className="text-[10px] text-green-600 mt-1.5">
                    ✅ Selected: {selectedApiLocation.name}
                  </p>
                )}
              </div>

              <div className="border-t border-gray-200 my-4"></div>

              {/* Get Location Button */}
              <div className="mb-4">
                <button
                  onClick={getCurrentLocation}
                  disabled={isGettingLocation}
                  className="w-full py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg shadow-green-500/25 transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGettingLocation ? (
                    <>
                      <FaSpinnerIcon className="w-4 h-4 animate-spin" />
                      Fetching Location...
                    </>
                  ) : (
                    <>
                      <FaMapMarkerAlt className="w-4 h-4" />
                      📍 Get Current Location
                    </>
                  )}
                </button>
                {locationError && (
                  <p className="text-xs text-red-500 mt-1.5 text-center">{locationError}</p>
                )}
                {(formData.latitude || formData.longitude) && (
                  <p className="text-[10px] text-green-600 mt-1 text-center">
                    ✅ Location fetched: {formData.latitude}, {formData.longitude}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600 block mb-1">Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="e.g., Timely Health"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Location Name *</label>
                  <input
                    type="text"
                    name="locationName"
                    value={formData.locationName}
                    onChange={handleInputChange}
                    placeholder="e.g., Head Office"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Admin Name</label>
                  <input
                    type="text"
                    name="adminName"
                    value={formData.adminName}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600 block mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Full address (auto-fills with Get Location or API)"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Latitude</label>
                  <input
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    placeholder="e.g., 28.6139"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Longitude</label>
                  <input
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    placeholder="e.g., 77.2090"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Expiry Time</label>
                  <select
                    name="expiryTime"
                    value={formData.expiryTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  >
                    <option value="15">15 seconds</option>
                    <option value="30">30 seconds</option>
                    <option value="45">45 seconds</option>
                    <option value="60">60 seconds</option>
                    <option value="120">120 seconds</option>
                    <option value="300">5 minutes</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">QR Size</label>
                  <select
                    name="qrSize"
                    value={formData.qrSize}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                  >
                    <option value="150">Small (150px)</option>
                    <option value="200">Medium (200px)</option>
                    <option value="250">Large (250px)</option>
                    <option value="300">XL (300px)</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  onClick={closePopup}
                  className="flex-1 py-3 rounded-xl text-sm font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateQR}
                  disabled={isGenerating}
                  className="flex-1 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/25 transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <FaSpinner className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FaQrcode className="w-4 h-4" />
                      {editingId ? 'Update QR Code' : 'Generate QR Code'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── VIEW QR POPUP ─── */}
      {viewingQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{viewingQR.companyName}</h3>
              <button
                onClick={closeViewQR}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col items-center">
              <div className="text-center mb-2">
                <p className="text-sm text-gray-500">{viewingQR.locationName}</p>
              </div>
              <div className="relative bg-white rounded-2xl p-4 border-2 border-gray-100">
                <QRCodeSVG 
                  id={`view-qr-${viewingQR.id}`}
                  value={viewingQR.qrData || JSON.stringify({ company: viewingQR.companyName })}
                  size={220}
                  level="H"
                  includeMargin={false}
                  bgColor="white"
                  fgColor="#1e293b"
                />
              </div>
              {viewingQR.address && (
                <p className="text-xs text-gray-400 mt-3 text-center">{viewingQR.address}</p>
              )}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => downloadQR(viewingQR.companyName, `view-qr-${viewingQR.id}`)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-500 text-white hover:bg-indigo-600 transition-colors flex items-center gap-1.5"
                >
                  <FaDownload className="w-3 h-3" /> Download
                </button>
                <button
                  onClick={() => printQR(viewingQR.companyName, `view-qr-${viewingQR.id}`)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1.5"
                >
                  <FaPrint className="w-3 h-3" /> Print
                </button>
                <button
                  onClick={() => shareQR(viewingQR.companyName, `view-qr-${viewingQR.id}`)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-cyan-500 text-white hover:bg-cyan-600 transition-colors flex items-center gap-1.5"
                >
                  <FaShare className="w-3 h-3" /> Share
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default QRScanner;